import { COSMETIC_BY_ID, COSMETIC_CATALOG, SHOP_TABS } from "@nano/content";
import type { CosmeticItem, EquipmentSlot } from "@nano/contracts";
import { useEffect, useState } from "react";
import { buyItem, closeOverlay, equipItem, previewItem, toggleWishlist } from "../../app/actions.ts";
import { shallowEqual, useApp } from "../../app/store.ts";
import { itemName, shopTabLabel } from "../../i18n/content.ts";
import { vi } from "../../i18n/vi.ts";
import { ownedItems, pendingGold } from "../../services/save/progress.ts";
import { AvatarPreview } from "../components/AvatarPreview.tsx";
import { Art, Button, Coins, Sheet } from "../components/common.tsx";

/**
 * Shop and wardrobe share one screen. Tapping a card only previews (free);
 * buying asks the server, which charges atomically and equips at once.
 */
export function Shop({ mode }: { mode: "shop" | "wardrobe" }) {
  const { save, outbox, preview, online, shopEnabled } = useApp(
    (s) => ({ save: s.save, outbox: s.outbox, preview: s.ui.previewEquip, online: s.ui.online, shopEnabled: s.ui.config.flags.shopEnabled }),
    shallowEqual,
  );
  const [tab, setTab] = useState<(typeof SHOP_TABS)[number]["id"]>("all");
  // Opening the shop from the wishlist goal lands on that item.
  const [selected, setSelected] = useState<string | null>(mode === "shop" ? (save?.wishlist ?? null) : null);
  const [confirming, setConfirming] = useState<CosmeticItem | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (selected) previewItem(selected);
    // Only the item pre-selected on open; later choices call previewItem directly.
  }, []);
  if (!save?.profile.avatarId) return null;

  const owned = new Set(ownedItems(save));
  const gold = save.server?.wallet.gold ?? 0;
  const items = COSMETIC_CATALOG.filter(
    (item) => item.isAvailable && (tab === "all" || item.slot === tab) && (mode === "shop" ? !item.starter : owned.has(item.id)),
  );
  const selectedItem = selected ? COSMETIC_BY_ID.get(selected) : undefined;

  const choose = (item: CosmeticItem) => {
    setSelected(item.id);
    previewItem(item.id);
  };

  const action = () => {
    if (!selectedItem) return null;
    const isOwned = owned.has(selectedItem.id);
    const wearing = save.equipped[selectedItem.slot] === selectedItem.id;
    if (isOwned) {
      return wearing ? (
        <Button variant="secondary" onClick={() => void equipItem(selectedItem.slot as EquipmentSlot, null)} testId="shop-remove">
          {vi.shop.takeOff}
        </Button>
      ) : (
        <Button onClick={() => void equipItem(selectedItem.slot as EquipmentSlot, selectedItem.id)} testId="shop-wear">
          {vi.shop.wear}
        </Button>
      );
    }
    const wished = save.wishlist === selectedItem.id;
    const wish = (
      <button
        type="button"
        className={"wish" + (wished ? " wish--on" : "")}
        onClick={() => void toggleWishlist(selectedItem.id)}
        aria-pressed={wished}
        data-testid="shop-wish"
      >
        <Art id="icon.heart" className="wish__icon" {...(wished ? { tint: "#ff7fae" } : {})} />
        {vi.shop.wishlist}
      </button>
    );
    if (!shopEnabled) return <p className="note">{vi.shop.disabled}</p>;
    if (!online)
      return (
        <>
          <p className="note">{vi.shop.offline}</p>
          {wish}
        </>
      );
    const short = gold < selectedItem.priceGold;
    return (
      <div className="shop__buy">
        <Button variant="gold" icon="reward.coin" disabled={short || busy} onClick={() => setConfirming(selectedItem)} testId="shop-buy">
          {short ? vi.shop.goalLeft(selectedItem.priceGold - gold) : vi.shop.buy(selectedItem.priceGold)}
        </Button>
        {wish}
      </div>
    );
  };

  return (
    <Sheet title={mode === "shop" ? vi.shop.title : vi.shop.wardrobe} onClose={() => closeOverlay()} className="shop" testId={mode === "shop" ? "shop" : "wardrobe"}>
      <div className="shop__stage">
        <AvatarPreview avatarId={save.profile.avatarId} equipped={preview ?? save.equipped} height={170} bounce={Boolean(preview)} />
        <div className="shop__wallet">
          <Coins amount={gold} pending={pendingGold(outbox)} />
          {selectedItem ? (
            <strong className="shop__selected">
              {itemName(selectedItem)}
              <span className={"rarity rarity--" + selectedItem.rarity}>{vi.shop.rarity[selectedItem.rarity]}</span>
            </strong>
          ) : (
            <span className="note">{vi.shop.preview}</span>
          )}
          {action()}
        </div>
      </div>
      <div className="tabs" role="tablist">
        {SHOP_TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={`tabs__tab ${tab === t.id ? "tabs__tab--active" : ""}`} onClick={() => setTab(t.id)}>
            {shopTabLabel(t)}
          </button>
        ))}
      </div>
      {items.length === 0 ? <p className="note">{vi.shop.empty}</p> : null}
      <div className="grid">
        {items.map((item) => {
          const isOwned = owned.has(item.id);
          const wearing = save.equipped[item.slot] === item.id;
          const status = wearing ? vi.shop.wearing : isOwned ? vi.shop.owned : gold < item.priceGold ? vi.shop.notEnough : null;
          const progress = Math.min(1, gold / Math.max(1, item.priceGold));
          return (
            <button
              key={item.id}
              type="button"
              className={`card card--${item.rarity} ${selected === item.id ? "card--selected" : ""} ${save.wishlist === item.id ? "card--wished" : ""}`}
              onClick={() => choose(item)}
              data-testid={`item-${item.id}`}
            >
              {item.rarity !== "common" ? <span className={"card__rarity rarity--" + item.rarity}>{vi.shop.rarity[item.rarity]}</span> : null}
              {save.wishlist === item.id ? <Art id="icon.heart" className="card__wish" tint="#ff7fae" /> : null}
              <Art id={item.assetIds.front} className="card__art" alt={itemName(item)} />
              <span className="card__name">{itemName(item)}</span>
              {isOwned ? (
                <span className={`chip ${wearing ? "chip--wearing" : "chip--owned"}`}>{status}</span>
              ) : (
                <span className="card__price">
                  <Art id="reward.coin" className="card__coin" />
                  {item.priceGold}
                  {status && progress < 0.75 ? <small className="card__status">{status}</small> : null}
                  {!isOwned && progress >= 0.75 && progress < 1 ? (
                    <small className="card__almost">
                      {vi.shop.almost}
                      <span className="card__almost-bar">
                        <span style={{ width: Math.round(progress * 100) + "%" }} />
                      </span>
                    </small>
                  ) : null}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {confirming ? (
        <Sheet title={vi.shop.title} onClose={() => setConfirming(null)} testId="buy-confirm">
          <div className="confirm">
            <Art id={confirming.assetIds.front} className="confirm__art" />
            <p>{vi.shop.confirm(itemName(confirming), confirming.priceGold)}</p>
            <Button
              variant="gold"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                await buyItem(confirming.id);
                setBusy(false);
                setConfirming(null);
              }}
              testId="buy-yes"
            >
              {vi.shop.confirmYes}
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(null)}>
              {vi.cancel}
            </Button>
          </div>
        </Sheet>
      ) : null}
    </Sheet>
  );
}
